import React, {
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react'

import {
	AuthTokens,
	ConfirmResetPasswordInput,
	confirmResetPassword,
	confirmSignIn,
	fetchAuthSession,
	getCurrentUser,
	signIn,
	signInWithRedirect,
	signOut,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { LOCAL_STORAGE_KEYS } from 'constants/enums/LocalStorageEnum'
import { authEventHandler } from 'utils/authUtils'
import logger from 'utils/logger'

import { updateInvitedUser } from 'services/email.service'

import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'
import { AWS_IDENTITY_PROVIDER } from '../config'

export interface IUser {
	email: string | null
	firstName: string
	lastName?: string | null
	session?: AuthTokens
}

export interface IAuthContextType {
	guestSignIn: () => Promise<void>
	handleConfirmResetPassword: ({
		username,
		confirmationCode,
		newPassword,
	}: ConfirmResetPasswordInput) => Promise<boolean>
	handleTempPasswordReset: (
		email: string,
		tempPassword: string,
		newPassword: string
	) => Promise<boolean>
	isAuthenticated: boolean
	isAuthenticating: boolean
	ssoSignIn: () => Promise<void>
	user: IUser
	userSignIn: (email: string, password: string) => Promise<void>
	userSignOut: () => Promise<void>
	userSignUp: (
		firstName: string,
		lastName: string,
		email: string,
		tempPassword: string,
		password: string
	) => Promise<void>
}

export const AuthContext = createContext<IAuthContextType | undefined>(
	undefined
)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [user, setUser] = useState<IUser>({
		email: null,
		lastName: '',
		firstName: 'login',
		session: undefined,
	})
	const [isAuthenticating, setIsAuthenticating] = useState(true)

	/**
	 * fetch currently logged-in user using AWS Auth library
	 * @returns {Promise<void>}
	 */
	const fetchAuthUser = async () => {
		try {
			await getCurrentUser()

			const { tokens: session } = await fetchAuthSession()
			const email = String(
				session?.idToken?.payload.email ?? 'guest@guest.com'
			)
			const firstName = String(
				session?.idToken?.payload.given_name ?? 'Guest'
			)
			const lastName = String(
				session?.idToken?.payload.family_name ?? 'Guest'
			)

			const fetchedUser: IUser = {
				email,
				lastName,
				firstName,
				session,
			}

			if (
				localStorage.getItem('SIGN_UP_STEP') === 'DONE' &&
				fetchedUser.lastName &&
				fetchedUser.email
			) {
				// Updates user's last name as a part of the work required to make
				// Submitted Ideas functional. This only occurs for new user signup.
				const response = await updateInvitedUser(
					fetchedUser.firstName,
					fetchedUser.lastName,
					fetchedUser.email
				)

				if (response.status === 200) {
					localStorage.removeItem('SIGN_UP_STEP')
				}
			}
			setIsAuthenticating(false)
			setUser(fetchedUser)
		} catch (error) {
			setIsAuthenticating(false)
			setUser({
				email: null,
				lastName: '',
				firstName: 'login',
				session: undefined,
			})
		}
	}

	useEffect(() => {
		fetchAuthUser()

		Hub.listen('auth', ({ payload }) => authEventHandler(payload.event))
	}, [])

	/**
	 * Attempts to sign in, handling the case where a guest session is already
	 * active. The app keeps a guest user signed in for unauthenticated browsing,
	 * but Amplify throws `UserAlreadyAuthenticatedException` if `signIn` is called
	 * while any session is active.
	 *
	 * The session is only cleared reactively (on that specific exception) rather
	 * than preemptively, so that a failed login attempt (e.g. wrong password)
	 * leaves the guest session intact and the user can continue browsing.
	 */
	const signInReplacingGuestSession = async (
		username: string,
		password: string | undefined
	) => {
		try {
			return await signIn({ username, password })
		} catch (error) {
			if (
				(error as { name?: string }).name ===
				'UserAlreadyAuthenticatedException'
			) {
				await signOut()
				return await signIn({ username, password })
			}
			throw error
		}
	}

	const guestSignIn = async () => {
		const username = process.env.REACT_APP_GUEST_USERNAME

		if (!username) {
			throw new Error('Guest Username must be provided')
		}

		try {
			await signIn({
				username,
				password: process.env.REACT_APP_GUEST_PASSWORD,
			})
		} catch (error) {
			logger.error('Guest Signin Error: ', error)
			throw new Error('Error occurred during Guest Sign In', error)
		}
	}

	const userSignIn = async (email: string, password: string) => {
		if (!email || !password) {
			throw new Error('User credentials must be provided')
		}

		try {
			await signInReplacingGuestSession(email, password)
		} catch (error) {
			logger.error('User Signin Error: ', error)
			throw new Error('Error occurred during user authentication', error)
		}
	}

	const userSignUp = async (
		firstName: string,
		lastName: string,
		email: string,
		tempPassword: string,
		password: string
	) => {
		if (!firstName && !lastName && !email && !password) {
			throw new Error('New user form input invalid or missing')
		}

		try {
			const { nextStep } = await signInReplacingGuestSession(
				email,
				tempPassword
			)

			if (
				nextStep.signInStep ===
				'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
			) {
				/**
				 * "pathname" is "/published-games/<GAME_ID>" since this is called
				 * within a modal thats opened within the published game component
				 */
				localStorage.setItem(
					LOCAL_STORAGE_KEYS.REDIRECT_PATH,
					`${window.location.pathname}?new_user=true`
				)
				const { nextStep } = await confirmSignIn({
					challengeResponse: password,
					options: {
						userAttributes: {
							given_name: `${firstName}`,
							family_name: `${lastName}`,
						},
					},
				})

				// This is a workaround to deal with the page navigation that occurs after
				// a user is authenticated. This can be removed when the auth flow is updated.
				if (nextStep.signInStep === 'DONE') {
					localStorage.setItem('SIGN_UP_STEP', nextStep.signInStep)
				}
			}
		} catch (error) {
			logger.error('UserSignUp Error: ', error)
			throw new Error('Error occurred during User Signup', error)
		}
	}

	const userSignOut = async () => signOut()

	const ssoSignIn = async () => {
		if (AWS_IDENTITY_PROVIDER !== undefined) {
			try {
				await signInWithRedirect({
					provider: { custom: AWS_IDENTITY_PROVIDER },
				})
			} catch (error) {
				if (
					(error as { name?: string }).name ===
					'UserAlreadyAuthenticatedException'
				) {
					await signOut()
					await signInWithRedirect({
						provider: { custom: AWS_IDENTITY_PROVIDER },
					})
				} else {
					logger.error('SSO Sign In Error: ', error)
					throw error
				}
			}
		} else {
			logger.error('Missing AWS Provider')
		}
	}

	const handleConfirmResetPassword = async ({
		username,
		confirmationCode,
		newPassword,
	}: ConfirmResetPasswordInput) => {
		try {
			await confirmResetPassword({
				username,
				confirmationCode,
				newPassword,
			})
			return true
		} catch (error) {
			logger.error('ConfirmResetPassword Error: ', error)
			throw new Error('Error occurred during ConfirmResetPassword', error)
		}
	}

	const handleTempPasswordReset = async (
		email: string,
		tempPassword: string,
		newPassword: string
	): Promise<boolean> => {
		try {
			// Always clear any existing session (e.g. guest) before starting
			// the challenge flow so the guest token cannot bleed into API calls
			// made during the NEW_PASSWORD_REQUIRED challenge window.
			await signOut()
			const { nextStep } = await signIn({
				username: email,
				password: tempPassword,
			})

			if (
				nextStep.signInStep !==
				'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
			) {
				throw new Error(
					`Unexpected sign-in step: ${nextStep.signInStep}`
				)
			}

			await confirmSignIn({ challengeResponse: newPassword })
			return true
		} catch (error) {
			logger.error('TempPasswordReset Error: ', error)
			// Restore the guest session so the app remains functional
			// if the user entered a wrong temp password or the flow failed.
			try {
				await guestSignIn()
			} catch (guestError) {
				logger.error(
					'Failed to restore guest session after TempPasswordReset failure: ',
					guestError
				)
			}
			throw new Error('Error occurred during TempPasswordReset', error)
		}
	}

	const value: IAuthContextType = {
		user,
		isAuthenticated: !!user && !!user.session,
		isAuthenticating,
		guestSignIn,
		userSignIn,
		userSignUp,
		userSignOut,
		ssoSignIn,
		handleConfirmResetPassword,
		handleTempPasswordReset,
	}

	if (isAuthenticating) {
		return <LoadingSpinner />
	}

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider

export const useAuth = () => {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider')
	}

	return context
}
